import { SearchIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';

export interface StockSearchResult {
    ticker: string;
    name: string;
    nameKo?: string;
}

interface StockSearchInputProps {
    onSelect: (stock: StockSearchResult) => void;
    excludeTickers?: string[];
    placeholder?: string;
    clearOnSelect?: boolean;
    dropdownDirection?: 'up' | 'down';
}

class ViewStore {
    searchQuery = '';
    searchResults: StockSearchResult[] = [];
    isSearching = false;
    isDropdownOpen = false;

    constructor() {
        makeAutoObservable(this);
    }

    setSearchQuery(query: string) {
        this.searchQuery = query;
        if (query.length >= 1) {
            this.search();
        } else {
            this.searchResults = [];
            this.isDropdownOpen = false;
        }
    }

    async search() {
        this.isSearching = true;
        this.isDropdownOpen = true;

        // TODO: API 연동 (추후 구현)
        // const results = await apiClient.stock.search.$query({ query: this.searchQuery });

        // 임시 목 데이터
        await new Promise((resolve) => setTimeout(resolve, 300));

        runInAction(() => {
            // 간단한 목 데이터 필터링
            const mockStocks: StockSearchResult[] = [
                { ticker: 'AAPL', name: 'Apple Inc.', nameKo: '애플' },
                { ticker: 'MSFT', name: 'Microsoft Corporation', nameKo: '마이크로소프트' },
                { ticker: 'GOOGL', name: 'Alphabet Inc.', nameKo: '구글' },
                { ticker: 'AMZN', name: 'Amazon.com Inc.', nameKo: '아마존' },
                { ticker: 'NVDA', name: 'NVIDIA Corporation', nameKo: '엔비디아' },
                { ticker: 'TSLA', name: 'Tesla Inc.', nameKo: '테슬라' },
                { ticker: 'META', name: 'Meta Platforms Inc.', nameKo: '메타' },
                { ticker: 'JNJ', name: 'Johnson & Johnson', nameKo: '존슨앤존슨' },
                { ticker: 'V', name: 'Visa Inc.', nameKo: '비자' },
                { ticker: 'WMT', name: 'Walmart Inc.', nameKo: '월마트' },
                { ticker: 'GM', name: 'General Motors', nameKo: '제너럴 모터스' },
                { ticker: 'F', name: 'Ford Motor Company', nameKo: '포드' },
                { ticker: 'VT', name: 'Vanguard Total World Stock ETF', nameKo: 'VT ETF' },
                { ticker: 'VOO', name: 'Vanguard S&P 500 ETF', nameKo: 'VOO ETF' },
                { ticker: 'SPY', name: 'SPDR S&P 500 ETF Trust', nameKo: 'SPY ETF' },
            ];

            const query = this.searchQuery.toLowerCase();
            this.searchResults = mockStocks.filter(
                (stock) =>
                    stock.ticker.toLowerCase().includes(query) ||
                    stock.name.toLowerCase().includes(query) ||
                    stock.nameKo?.includes(this.searchQuery),
            );
            this.isSearching = false;
        });
    }

    closeDropdown() {
        this.isDropdownOpen = false;
    }

    clearSearch() {
        this.searchQuery = '';
        this.searchResults = [];
        this.isDropdownOpen = false;
    }
}

const StockSearchInput = observer(
    forwardRef<HTMLInputElement, StockSearchInputProps>(
        (
            {
                onSelect,
                excludeTickers = [],
                placeholder = '종목 검색 (티커 또는 종목명)',
                clearOnSelect = false,
                dropdownDirection = 'down',
            },
            ref,
        ) => {
            const viewStore = useMobxInstance(ViewStore);
            const inputRef = useRef<HTMLInputElement>(null);
            const containerRef = useRef<HTMLDivElement>(null);
            const [isFocused, setIsFocused] = useState(false);

            useImperativeHandle(ref, () => inputRef.current!);

            // 외부 클릭 감지
            useEffect(() => {
                const handleClickOutside = (event: MouseEvent) => {
                    if (
                        containerRef.current &&
                        !containerRef.current.contains(event.target as Node)
                    ) {
                        viewStore.closeDropdown();
                        setIsFocused(false);
                    }
                };

                document.addEventListener('mousedown', handleClickOutside);
                return () => {
                    document.removeEventListener('mousedown', handleClickOutside);
                };
            }, [viewStore]);

            const handleSelect = (stock: StockSearchResult) => {
                onSelect(stock);
                if (clearOnSelect) {
                    viewStore.clearSearch();
                } else {
                    viewStore.closeDropdown();
                }
                setIsFocused(false);
            };

            const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                const filteredResults = viewStore.searchResults.filter(
                    (stock) => !excludeTickers.includes(stock.ticker),
                );

                if (e.key === 'Enter' && filteredResults.length > 0) {
                    e.preventDefault();
                    handleSelect(filteredResults[0]);
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    viewStore.closeDropdown();
                    inputRef.current?.blur();
                }
            };

            const filteredResults = viewStore.searchResults
                .filter((stock) => !excludeTickers.includes(stock.ticker))
                .slice(0, 4); // 최대 4개만 표시

            return (
                <div ref={containerRef} className="relative">
                    {/* 검색 입력창 */}
                    <div className="relative">
                        <SearchIcon className="text-base-content/40 absolute top-1/2 left-3 z-10 size-5 -translate-y-1/2" />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={placeholder}
                            value={viewStore.searchQuery}
                            className="input input-bordered w-full pl-10"
                            onChange={(e) => viewStore.setSearchQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onFocus={() => {
                                setIsFocused(true);
                                if (viewStore.searchQuery.length >= 1) {
                                    viewStore.search();
                                }
                            }}
                        />
                    </div>

                    {/* 검색 결과 드롭다운 */}
                    {isFocused && viewStore.isDropdownOpen && (
                        <div
                            className={`bg-base-100 border-base-300 absolute z-10 w-full rounded-lg border shadow-lg ${
                                dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
                            }`}
                        >
                            {viewStore.isSearching ? (
                                <div className="flex items-center justify-center py-4">
                                    <span className="loading loading-spinner loading-sm"></span>
                                </div>
                            ) : filteredResults.length > 0 ? (
                                <ul className="menu rounded-lg p-1">
                                    {filteredResults.map((stock) => (
                                        <li key={stock.ticker}>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleSelect(stock);
                                                }}
                                            >
                                                <div className="flex w-full items-center justify-between">
                                                    <div>
                                                        <div className="font-semibold">
                                                            {stock.ticker}
                                                        </div>
                                                        <div className="text-base-content/60 text-xs">
                                                            {stock.nameKo || stock.name}
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-base-content/60 py-4 text-center text-sm">
                                    검색 결과가 없습니다
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        },
    ),
);

StockSearchInput.displayName = 'StockSearchInput';

export default StockSearchInput;
